import { X, Home, Heart, Star, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SignupModal from './SignupModal';
import logger from '../utils/logger';
// import { SessionManager } from '../utils/session-manager';
import { googleSheetsService } from '../utils/google-sheets';

// Inspirational quotes about dreams and home ownership
const inspirationalQuotes = [
  {
    quote: "A house is made of walls and beams; a home is built with love and dreams.",
    author: "Ralph Waldo Emerson"
  },
  {
    quote: "The magic thing about home is that it feels good to leave, and it feels even better to come back.",
    author: "Wendy Wunder"
  },
  {
    quote: "Home is where your story begins. Let us help you write the perfect chapter.",
    author: "Anonymous"
  },
  {
    quote: "Every dream home starts with a single conversation. Let's start yours today.",
    author: "Anonymous"
  },
  {
    quote: "Your home should tell the story of who you are, and be a collection of what you love.",
    author: "Nate Berkus"
  },
  {
    quote: "The best investment you can make is in your own home. Let's build your future together.",
    author: "Anonymous"
  },
  {
    quote: "Home is not a place, it's a feeling. Let's create that feeling for you.",
    author: "Anonymous"
  },
  {
    quote: "Dreams don't work unless you do. Let's work together to make your dream home a reality.",
    author: "Anonymous"
  }
];

const AppointmentModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    location: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [showSignup, setShowSignup] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticatedPhone, setAuthenticatedPhone] = useState('');
  const [currentQuote, setCurrentQuote] = useState(inspirationalQuotes[0]);

  // Set random quote when modal opens
  useEffect(() => {
    if (isOpen) {
      const randomIndex = Math.floor(Math.random() * inspirationalQuotes.length);
      setCurrentQuote(inspirationalQuotes[randomIndex]);
    }
  }, [isOpen]);

  // Name validation (letters, spaces, and common name characters)
  const isValidName = (name) => {
    // Allow letters, spaces, hyphens, apostrophes, and dots
    const nameRegex = /^[a-zA-Z\s\-'.]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (!isValidName(formData.fullName)) {
      newErrors.fullName = 'Please enter a valid name (letters, spaces, hyphens, apostrophes only)';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      logger.info('User not authenticated, showing signup modal');
      setShowSignup(true);
      return;
    }

    // Appointment limit check commented out for testing
    /*
    if (!SessionManager.canBookAppointment(authenticatedPhone)) {
      setSubmitStatus({
        type: 'error',
        message: 'Session expired or appointment limit reached. Please try again later.'
      });
      return;
    }
    */

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    if (validateForm()) {
      try {
        // const timestamp = new Date().toISOString();
        // const appointmentId = `APPT_${Date.now()}`;
        
        // Submit appointment to Google Sheets
        try {
          await googleSheetsService.submitAppointment({
            fullName: formData.fullName,
            mobileNumber: authenticatedPhone,
            location: formData.location
          });
          
          setSubmitStatus({
            type: 'success',
            message: 'Appointment booked successfully! We will contact you soon.'
          });
        } catch (sheetsError) {
          logger.error('Google Sheets submission failed:', sheetsError);
          throw new Error('Failed to book appointment. Please try again later.');
        }

        setTimeout(() => {
          onClose();
          setSubmitStatus({ type: '', message: '' });
        }, 2000);
      } catch (error) {
        logger.error('Appointment submission error:', error);
        setSubmitStatus({
          type: 'error',
          message: error.message || 'Failed to book appointment. Please try again.'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSignupSuccess = (phoneNumber) => {
    logger.info('Signup successful:', phoneNumber);
    setIsAuthenticated(true);
    setAuthenticatedPhone(phoneNumber);
    setShowSignup(false);
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            key="appointment-modal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-50 flex items-center justify-center pt-20 pb-1 px-1 sm:pt-24 sm:pb-2 sm:px-2 md:pt-24 md:pb-4 md:px-4 bg-gray-900/70 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            <div
              className="relative w-full max-w-[95vw] sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl bg-white rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 max-h-[75vh] sm:max-h-[80vh] md:max-h-[85vh] lg:max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={onClose}
                className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full 
                  transition-colors z-10"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>

              {/* Beautiful Header with Inspirational Quote */}
              <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 pt-6 pb-4 px-4 sm:pt-8 sm:pb-6 sm:px-6 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-4 left-4 w-8 h-8 border-2 border-white/30 rounded-full"></div>
                  <div className="absolute top-8 right-8 w-6 h-6 border-2 border-white/20 rounded-full"></div>
                  <div className="absolute bottom-6 left-8 w-4 h-4 border-2 border-white/25 rounded-full"></div>
                  <div className="absolute bottom-4 right-4 w-10 h-10 border-2 border-white/15 rounded-full"></div>
                </div>
                
                {/* Quote Section */}
                <div className="relative z-10 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                      <Home className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <motion.div
                    key={currentQuote.quote}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-4"
                  >
                    <blockquote className="text-white text-base sm:text-lg md:text-xl font-medium leading-relaxed mb-3">
                      &ldquo;{currentQuote.quote}&rdquo;
                    </blockquote>
                    <cite className="text-white/80 text-sm italic">
                      — {currentQuote.author}
                    </cite>
                  </motion.div>
                  
                  <div className="flex justify-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-300 fill-current" />
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-6 md:p-6 lg:p-8">
                <div className="text-center mb-6">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-4"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
                      <Sparkles className="w-8 h-8 text-indigo-600" />
                    </div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3">
                      Start Your Dream Journey
                    </h2>
                    <p className="text-gray-600 text-base leading-relaxed">
                      Let&apos;s turn your vision into reality with a personalized consultation
                    </p>
                  </motion.div>
                  
                  {authenticatedPhone && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 }}
                      className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-full text-sm text-green-700 mb-4"
                    >
                      <Heart className="w-4 h-4 mr-2 fill-current" />
                      Verified: {authenticatedPhone}
                    </motion.div>
                  )}
                </div>

                <motion.form 
                  onSubmit={handleSubmit} 
                  className="space-y-6 w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className={`w-full px-4 py-3 rounded-xl border-2 text-base transition-all duration-300 ${
                        errors.fullName 
                          ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-200 bg-gray-50 focus:border-indigo-500 focus:ring-indigo-200 focus:bg-white'
                      } focus:ring-4 focus:outline-none`}
                    />
                    {errors.fullName && (
                      <motion.p 
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-red-600 flex items-center"
                      >
                        <span className="mr-1">⚠️</span>
                        {errors.fullName}
                      </motion.p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Project Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Where is your dream project located?"
                      className={`w-full px-4 py-3 rounded-xl border-2 text-base transition-all duration-300 ${
                        errors.location 
                          ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-200 bg-gray-50 focus:border-indigo-500 focus:ring-indigo-200 focus:bg-white'
                      } focus:ring-4 focus:outline-none`}
                    />
                    {errors.location && (
                      <motion.p 
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-red-600 flex items-center"
                      >
                        <span className="mr-1">⚠️</span>
                        {errors.location}
                      </motion.p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl
                               shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-base
                               flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Creating Your Dream...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        <span>Start My Dream Project</span>
                      </>
                    )}
                  </motion.button>

                  {submitStatus.message && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-4 p-4 text-center rounded-xl border-2 ${
                        submitStatus.type === 'success' 
                          ? 'bg-green-50 text-green-800 border-green-200' 
                          : 'bg-red-50 text-red-800 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {submitStatus.type === 'success' ? (
                          <span className="text-green-600">🎉</span>
                        ) : (
                          <span className="text-red-600">⚠️</span>
                        )}
                        <span className="font-medium">{submitStatus.message}</span>
                      </div>
                    </motion.div>
                  )}
                </motion.form>

                {/* Beautiful Stats Section */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-8 pt-6 border-t border-gray-100"
                >
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-500 font-medium">Trusted by Dream Builders</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl">
                      <p className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                        25+
                      </p>
                      <p className="text-xs text-gray-600 font-medium">Dream Homes</p>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                      <p className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        100%
                      </p>
                      <p className="text-xs text-gray-600 font-medium">Happy Families</p>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
                      <p className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                        5+
                      </p>
                      <p className="text-xs text-gray-600 font-medium">Years Experience</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SignupModal 
        isOpen={showSignup}
        onClose={() => setShowSignup(false)}
        onSignupSuccess={handleSignupSuccess}
      />
    </>
  );
};

AppointmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default AppointmentModal;
